import {
  ChangeEvent,
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {motion} from 'framer-motion';
import {useDropDown} from '../../../../hooks';
import {Label} from '../../../atoms/label';
import {FieldWrapper} from '../../../atoms/field-wrapper';
import {Helper} from '../../../atoms/helper';
import {
  Wrapper,
  DropdownWrapper,
  DropWrapper,
  DropdownHandler,
  HandlerWrapper,
  ArrowIconWrapper,
  OptionsWrapper,
  OptionWrapper,
} from './styled';
import {Groups} from '../../groups';
import {DropdownCard} from '../../dropdown-card';
import {Body1, Body2} from '../../../atoms/typography';
import {ArrowHeadDownIcon} from '../../../atoms/icons/arrow-head-down';
import {
  MultiOptionsSelectField2Props,
  SelectOptionItem,
} from './types';

export const MultiOptionsSelectField2 = forwardRef(
  (
    {
      label,
      state,
      helper,
      placeholder,
      options = [],
      onChange,
      defaultValue,
      disabled,
      searchPlaceholder,
      NoDataPlaceholder,
    }: MultiOptionsSelectField2Props,
    _ref: Ref<any>,
  ) => {
    const {handleOpen, ref: dropRef, open} = useDropDown();
    const [searchText, setSearchText] = useState('');

    const filterOptions = useMemo<SelectOptionItem[]>(() => {
      if (searchText.trim() === '') return options;
      return options.filter((option) =>
        option.label.toLowerCase().includes(searchText.toLowerCase()),
      );
    }, [options, searchText]);

    const defaultSelections = useMemo<SelectOptionItem[]>(() => {
      if (!defaultValue?.length) return [];
      const defaultIds = defaultValue.map((val) => val.value);
      return options.filter((option) => defaultIds.includes(option.value));
    }, [defaultValue, options]);

    const [selectedOptions, setSelectedOptions] = useState<SelectOptionItem[]>(
      defaultSelections,
    );

    useEffect(() => {
      setSelectedOptions(defaultSelections);
    }, [defaultSelections]);

    const handleChange = (option: SelectOptionItem) => {
      const alreadySelected = selectedOptions.some(
        (select) => select.value === option.value,
      );
      const nextSelection = alreadySelected
        ? selectedOptions.filter((select) => select.value !== option.value)
        : [...selectedOptions, option];

      setSelectedOptions(nextSelection);
      onChange?.(nextSelection);
    };

    const handleCancelFx = useCallback(
      (value: string) => {
        const remaining = selectedOptions.filter(
          (option) => option.value !== value,
        );
        setSelectedOptions(remaining);
        onChange?.(remaining);
      },
      [selectedOptions, onChange],
    );

    const hasSelections = selectedOptions.length > 0;
    const noDataCopy = NoDataPlaceholder || 'No options available';

    return (
      <FieldWrapper margin={true}>
        <Wrapper>
          <Label aria-label="label">{label}</Label>
          <DropWrapper ref={dropRef}>
            <DropdownHandler
              open={open}
              onClick={handleOpen}
              disabled={disabled}>
              <HandlerWrapper>
                <OptionsWrapper disabled={disabled}>
                  {hasSelections ? (
                    <Groups
                      total={selectedOptions.length}
                      items={selectedOptions.map((item) => ({
                        id: String(item.value),
                        name: item.label,
                        value: item.value,
                      }))}
                      disabled={disabled}
                      cancelFx={!disabled ? handleCancelFx : undefined}
                    />
                  ) : (
                    <p className="greyOut">{placeholder}</p>
                  )}
                </OptionsWrapper>
                <ArrowIconWrapper disabled={disabled}>
                  <motion.span
                    initial={{rotate: 0}}
                    animate={{
                      rotate: !disabled && open ? 180 : 0,
                      marginTop: !disabled && open ? '-6px' : '0px',
                    }}
                    transition={{
                      duration: 0.3,
                    }}
                    key="multi-select-2-toggled">
                    <ArrowHeadDownIcon />
                  </motion.span>
                </ArrowIconWrapper>
              </HandlerWrapper>
            </DropdownHandler>
            <div style={{height: '16px'}} />
            {open && (
              <DropdownCard
                disableFooter={true}
                disableHeader={true}
                width="100%"
                placeholder={searchPlaceholder ?? ''}
                onClickCancel={handleOpen}
                onSubmit={handleOpen}
                handleSearch={(event: ChangeEvent<HTMLInputElement>) =>
                  setSearchText(event.target.value)
                }
                title="Group">
                <>
                  {filterOptions.length > 0 ? (
                    filterOptions.map((option) => (
                      <OptionWrapper
                        key={option.value}
                        onClick={() => handleChange(option)}
                        active={selectedOptions.some(
                          (selected) => selected.value === option.value,
                        )}>
                        <Body2>{option.label}</Body2>
                      </OptionWrapper>
                    ))
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '12px',
                      }}>
                      <Body1 kind="textDark">{noDataCopy}</Body1>
                    </div>
                  )}
                </>
              </DropdownCard>
            )}
          </DropWrapper>
          <Helper aria-label="helper" state={state} children={helper} />
        </Wrapper>
      </FieldWrapper>
    );
  },
);
